import { ChecklistModuleDefinition } from '../../types/due-diligence';

export const landBasicsModule: ChecklistModuleDefinition = {
  code: 'land_basics',
  name: 'Land Basics',
  description: 'Property identification, ownership, zoning, and boundaries',
  categories: [
    {
      code: 'property_identification',
      name: 'Property Identification',
      items: [
        {
          code: 'acreage_verify',
          title: 'Verify acreage/lot size',
          description: 'Confirm actual acreage matches listing',
          isGroupTask: false,
          dataFields: [
            { key: 'listed_acres', label: 'Listed Acres', type: 'number', unit: 'acres' },
            { key: 'verified_acres', label: 'Verified Acres', type: 'number', unit: 'acres' },
            { key: 'source', label: 'Verification Source', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Significant discrepancy from listing (>5%)' },
            { flag: 'yellow', condition: 'Minor discrepancy from listing (2-5%)' },
          ],
        },
        {
          code: 'parcel_numbers',
          title: 'Confirm parcel number(s)',
          description: 'Get official parcel/APN numbers from county',
          isGroupTask: false,
          dataFields: [
            { key: 'parcel_numbers', label: 'Parcel Numbers', type: 'array' },
            { key: 'county', label: 'County', type: 'string' },
          ],
        },
        {
          code: 'legal_description',
          title: 'Legal description review',
          description: 'Review and understand the legal description',
          isGroupTask: false,
          dataFields: [
            { key: 'legal_description', label: 'Legal Description', type: 'string' },
            { key: 'complexity_notes', label: 'Complexity Notes', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Complex or unclear legal description' },
          ],
        },
        {
          code: 'address_verify',
          title: 'Property address verification',
          description: 'Confirm 911 address if exists',
          isGroupTask: false,
          dataFields: [
            { key: 'physical_address', label: 'Physical Address', type: 'string' },
            { key: 'mailing_address', label: 'Mailing Address', type: 'string' },
            { key: 'has_911_address', label: 'Has 911 Address', type: 'boolean' },
          ],
        },
        {
          code: 'gps_coords',
          title: 'GPS coordinates',
          description: 'Get exact property coordinates',
          isGroupTask: false,
          dataFields: [
            { key: 'latitude', label: 'Latitude', type: 'number' },
            { key: 'longitude', label: 'Longitude', type: 'number' },
          ],
        },
        {
          code: 'jurisdiction',
          title: 'County/jurisdiction',
          description: 'Identify governing county/municipality',
          isGroupTask: false,
          dataFields: [
            { key: 'county', label: 'County', type: 'string' },
            { key: 'municipality', label: 'Municipality', type: 'string' },
            { key: 'jurisdiction_notes', label: 'Notes', type: 'string' },
          ],
        },
      ],
    },
    {
      code: 'ownership_title',
      name: 'Ownership & Title',
      items: [
        {
          code: 'owner_verify',
          title: 'Current owner verification',
          description: 'Confirm current legal owner matches seller',
          isGroupTask: false,
          dataFields: [
            { key: 'owner_name', label: 'Owner Name', type: 'string' },
            { key: 'owner_type', label: 'Owner Type', type: 'select', options: [
              { value: 'individual', label: 'Individual' },
              { value: 'joint', label: 'Joint Ownership' },
              { value: 'trust', label: 'Trust' },
              { value: 'llc', label: 'LLC' },
              { value: 'corporation', label: 'Corporation' },
              { value: 'estate', label: 'Estate' },
            ]},
            { key: 'ownership_since', label: 'Owned Since', type: 'date' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Owner does not match seller' },
            { flag: 'yellow', condition: 'Complex ownership structure' },
          ],
        },
        {
          code: 'title_search',
          title: 'Title search',
          description: 'Order/review preliminary title search',
          isGroupTask: false,
          dataFields: [
            { key: 'title_company', label: 'Title Company', type: 'string' },
            { key: 'search_date', label: 'Search Date', type: 'date' },
            { key: 'issues_found', label: 'Issues Found', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Major title defects' },
            { flag: 'red', condition: 'Title issues requiring resolution' },
            { flag: 'green', condition: 'Clean title' },
          ],
        },
        {
          code: 'liens_check',
          title: 'Liens check',
          description: 'Check for outstanding liens',
          isGroupTask: false,
          dataFields: [
            { key: 'liens_found', label: 'Liens Found', type: 'boolean' },
            { key: 'lien_types', label: 'Lien Types', type: 'array' },
            { key: 'total_lien_amount', label: 'Total Lien Amount', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Outstanding liens exceeding value' },
            { flag: 'red', condition: 'Outstanding liens present' },
            { flag: 'green', condition: 'No liens' },
          ],
        },
        {
          code: 'back_taxes',
          title: 'Back taxes check',
          description: 'Verify no delinquent property taxes',
          isGroupTask: false,
          dataFields: [
            { key: 'taxes_current', label: 'Taxes Current', type: 'boolean' },
            { key: 'amount_owed', label: 'Amount Owed', type: 'number', unit: 'dollars' },
            { key: 'years_delinquent', label: 'Years Delinquent', type: 'number' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Delinquent taxes owed' },
            { flag: 'green', condition: 'Taxes current' },
          ],
        },
        {
          code: 'judgments',
          title: 'Judgment search',
          description: 'Check for judgments against property/owner',
          isGroupTask: false,
          dataFields: [
            { key: 'judgments_found', label: 'Judgments Found', type: 'boolean' },
            { key: 'judgment_details', label: 'Details', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Judgments attached to property' },
          ],
        },
        {
          code: 'chain_of_title',
          title: 'Chain of title review',
          description: 'Review ownership history',
          isGroupTask: false,
          dataFields: [
            { key: 'years_reviewed', label: 'Years Reviewed', type: 'number' },
            { key: 'transfers_count', label: 'Number of Transfers', type: 'number' },
            { key: 'issues', label: 'Issues Found', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Frequent ownership changes' },
            { flag: 'red', condition: 'Breaks in chain of title' },
          ],
        },
      ],
    },
    {
      code: 'zoning_land_use',
      name: 'Zoning & Land Use',
      items: [
        {
          code: 'zoning_current',
          title: 'Current zoning classification',
          description: 'Identify current zoning designation',
          isGroupTask: false,
          dataFields: [
            { key: 'zoning_code', label: 'Zoning Code', type: 'string' },
            { key: 'zoning_description', label: 'Description', type: 'string' },
          ],
        },
        {
          code: 'zoning_permitted',
          title: 'Permitted uses',
          description: 'What uses are allowed by-right',
          isGroupTask: false,
          dataFields: [
            { key: 'permitted_uses', label: 'Permitted Uses', type: 'array' },
            { key: 'intended_use_permitted', label: 'Intended Use Permitted', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Intended use not permitted' },
            { flag: 'big_green', condition: 'Intended use permitted by-right' },
          ],
        },
        {
          code: 'zoning_conditional',
          title: 'Conditional uses',
          description: 'What uses require special permit',
          isGroupTask: false,
          dataFields: [
            { key: 'conditional_uses', label: 'Conditional Uses', type: 'array' },
            { key: 'permit_process', label: 'Permit Process', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Intended use requires conditional permit' },
          ],
        },
        {
          code: 'setbacks',
          title: 'Setback requirements',
          description: 'Building setback requirements from boundaries',
          isGroupTask: false,
          dataFields: [
            { key: 'front_setback', label: 'Front Setback', type: 'number', unit: 'feet' },
            { key: 'rear_setback', label: 'Rear Setback', type: 'number', unit: 'feet' },
            { key: 'side_setback', label: 'Side Setback', type: 'number', unit: 'feet' },
            { key: 'road_setback', label: 'Road Setback', type: 'number', unit: 'feet' },
          ],
        },
        {
          code: 'lot_coverage',
          title: 'Lot coverage limits',
          description: 'Maximum building/impervious coverage allowed',
          isGroupTask: false,
          dataFields: [
            { key: 'max_coverage_percent', label: 'Max Coverage %', type: 'number', unit: 'percent' },
            { key: 'max_impervious', label: 'Max Impervious', type: 'number', unit: 'percent' },
          ],
        },
        {
          code: 'height_limits',
          title: 'Height restrictions',
          description: 'Maximum building height allowed',
          isGroupTask: false,
          dataFields: [
            { key: 'max_height_feet', label: 'Max Height', type: 'number', unit: 'feet' },
            { key: 'max_stories', label: 'Max Stories', type: 'number' },
          ],
        },
        {
          code: 'min_lot_size',
          title: 'Minimum lot size',
          description: 'Minimum acres for subdivision/development',
          isGroupTask: false,
          dataFields: [
            { key: 'min_lot_acres', label: 'Min Lot Size', type: 'number', unit: 'acres' },
          ],
        },
        {
          code: 'rezone_potential',
          title: 'Rezoning potential',
          description: 'Assess possibility of rezoning if needed',
          isGroupTask: false,
          dataFields: [
            { key: 'rezone_needed', label: 'Rezoning Needed', type: 'boolean' },
            { key: 'target_zoning', label: 'Target Zoning', type: 'string' },
            { key: 'likelihood', label: 'Likelihood', type: 'select', options: [
              { value: 'high', label: 'High' },
              { value: 'medium', label: 'Medium' },
              { value: 'low', label: 'Low' },
              { value: 'unlikely', label: 'Unlikely' },
            ]},
            { key: 'process_notes', label: 'Process Notes', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Rezoning unlikely but needed' },
            { flag: 'yellow', condition: 'Rezoning may be needed' },
          ],
        },
      ],
    },
    {
      code: 'tax_assessment',
      name: 'Tax Assessment',
      items: [
        {
          code: 'tax_assessment',
          title: 'Current tax assessment',
          description: 'Review current assessed value breakdown',
          isGroupTask: false,
          dataFields: [
            { key: 'land_value', label: 'Land Value', type: 'number', unit: 'dollars' },
            { key: 'improvement_value', label: 'Improvement Value', type: 'number', unit: 'dollars' },
            { key: 'total_assessed', label: 'Total Assessed', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'annual_taxes',
          title: 'Annual property taxes',
          description: 'Current annual tax amount',
          isGroupTask: false,
          dataFields: [
            { key: 'annual_amount', label: 'Annual Amount', type: 'number', unit: 'dollars' },
            { key: 'tax_rate', label: 'Tax Rate', type: 'number', unit: 'percent' },
          ],
        },
        {
          code: 'tax_vs_asking',
          title: 'Tax assessment vs asking',
          description: 'Compare assessment to asking price',
          isGroupTask: false,
          dataFields: [
            { key: 'asking_price', label: 'Asking Price', type: 'number', unit: 'dollars' },
            { key: 'assessment_ratio', label: 'Assessment Ratio', type: 'number', unit: 'percent' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Asking significantly below market (good deal)' },
            { flag: 'yellow', condition: 'Assessment significantly below asking (tax increase likely)' },
          ],
        },
        {
          code: 'tax_history',
          title: 'Tax history',
          description: 'Review tax history and trends',
          isGroupTask: false,
          dataFields: [
            { key: 'years_reviewed', label: 'Years Reviewed', type: 'number' },
            { key: 'trend', label: 'Trend', type: 'select', options: [
              { value: 'increasing', label: 'Increasing' },
              { value: 'stable', label: 'Stable' },
              { value: 'decreasing', label: 'Decreasing' },
            ]},
            { key: 'avg_annual_increase', label: 'Avg Annual Increase', type: 'number', unit: 'percent' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Taxes increasing rapidly (>5%/yr)' },
          ],
        },
        {
          code: 'special_assessments',
          title: 'Special assessments',
          description: 'Any special tax districts or assessments',
          isGroupTask: false,
          dataFields: [
            { key: 'has_special_assessments', label: 'Has Special Assessments', type: 'boolean' },
            { key: 'assessment_types', label: 'Assessment Types', type: 'array' },
            { key: 'annual_amount', label: 'Annual Amount', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Special assessments present' },
          ],
        },
        {
          code: 'ag_tax_status',
          title: 'Agricultural tax status',
          description: 'Check for ag exemption and rollback risk',
          isGroupTask: false,
          dataFields: [
            { key: 'has_ag_exemption', label: 'Has Ag Exemption', type: 'boolean' },
            { key: 'exemption_type', label: 'Exemption Type', type: 'string' },
            { key: 'rollback_risk', label: 'Rollback Risk', type: 'boolean' },
            { key: 'rollback_years', label: 'Rollback Years', type: 'number' },
            { key: 'estimated_rollback', label: 'Est. Rollback Amount', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Ag exemption in place, will maintain' },
            { flag: 'red', condition: 'Ag rollback taxes due on change of use' },
            { flag: 'yellow', condition: 'Ag exemption status unclear' },
          ],
        },
      ],
    },
    {
      code: 'survey_boundaries',
      name: 'Survey & Boundaries',
      items: [
        {
          code: 'survey_existing',
          title: 'Existing survey review',
          description: 'Review any existing survey documents',
          isGroupTask: false,
          dataFields: [
            { key: 'has_survey', label: 'Has Existing Survey', type: 'boolean' },
            { key: 'survey_date', label: 'Survey Date', type: 'date' },
            { key: 'surveyor', label: 'Surveyor', type: 'string' },
            { key: 'survey_type', label: 'Survey Type', type: 'select', options: [
              { value: 'boundary', label: 'Boundary Survey' },
              { value: 'alta', label: 'ALTA Survey' },
              { value: 'topographic', label: 'Topographic' },
              { value: 'mortgage', label: 'Mortgage Survey' },
            ]},
          ],
        },
        {
          code: 'survey_needed',
          title: 'Survey needed assessment',
          description: 'Determine if new survey is required',
          isGroupTask: false,
          dataFields: [
            { key: 'survey_recommended', label: 'Survey Recommended', type: 'boolean' },
            { key: 'survey_type_needed', label: 'Type Needed', type: 'string' },
            { key: 'estimated_cost', label: 'Estimated Cost', type: 'number', unit: 'dollars' },
            { key: 'reasoning', label: 'Reasoning', type: 'string' },
          ],
        },
        {
          code: 'boundary_markers',
          title: 'Boundary markers',
          description: 'Check for visible boundary markers/pins',
          isGroupTask: false,
          dataFields: [
            { key: 'markers_found', label: 'Markers Found', type: 'boolean' },
            { key: 'marker_type', label: 'Marker Type', type: 'string' },
            { key: 'marker_condition', label: 'Condition', type: 'select', options: [
              { value: 'good', label: 'Good - Clearly visible' },
              { value: 'fair', label: 'Fair - Some found' },
              { value: 'poor', label: 'Poor - Few/none found' },
            ]},
          ],
        },
        {
          code: 'boundary_disputes',
          title: 'Boundary disputes',
          description: 'Check for any known boundary issues',
          isGroupTask: false,
          dataFields: [
            { key: 'disputes_exist', label: 'Disputes Exist', type: 'boolean' },
            { key: 'dispute_details', label: 'Details', type: 'string' },
            { key: 'neighbors_involved', label: 'Neighbors Involved', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Active boundary dispute' },
            { flag: 'red', condition: 'History of boundary disputes' },
            { flag: 'green', condition: 'No disputes, good neighbor relations' },
          ],
        },
        {
          code: 'encroachments',
          title: 'Encroachments',
          description: 'Check for encroachments in either direction',
          isGroupTask: false,
          dataFields: [
            { key: 'encroachments_found', label: 'Encroachments Found', type: 'boolean' },
            { key: 'encroachment_type', label: 'Type', type: 'select', options: [
              { value: 'onto_property', label: 'Neighbor onto this property' },
              { value: 'onto_neighbor', label: 'This property onto neighbor' },
              { value: 'both', label: 'Both directions' },
            ]},
            { key: 'encroachment_details', label: 'Details', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Major encroachment affecting use' },
            { flag: 'red', condition: 'Encroachments present' },
            { flag: 'green', condition: 'No encroachments' },
          ],
        },
      ],
    },
  ],
};

